import React, { useState } from "react";
import { Link } from "react-router-dom";
import { EVENT_CONCEPTS } from "../data/content";
import { Reveal } from "./Ornament";

/** Fan-layout concept spread — cards arranged like a hand of cards,
 *  center upright, sides rotated outward. Hover lifts a card upright. */
export default function ConceptsFan() {
  // Take up to 7 for a nice spread
  const cards = EVENT_CONCEPTS.slice(0, 7);
  const total = cards.length;
  const center = (total - 1) / 2;
  const [hover, setHover] = useState(null);

  const slugify = (name) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  return (
    <section
      id="concepts-fan"
      className="relative bg-[var(--lu-ivory)] section-y overflow-hidden"
      data-testid="concepts-fan-section"
    >
      {/* Radial gold backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 left-1/2 -translate-x-1/2 w-[1400px] h-[900px] opacity-40"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(201,160,80,0.25), transparent 65%)",
        }}
      />

      <div className="container-lux relative">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3">
              <span className="inline-block h-1.5 w-1.5 rotate-45 bg-[var(--lu-gold-dark)]" aria-hidden />
              <p className="kicker text-[var(--lu-gold-dark)]">Signature Concepts</p>
              <span className="inline-block h-1.5 w-1.5 rotate-45 bg-[var(--lu-gold-dark)]" aria-hidden />
            </div>
            <h2 className="mt-6 h-editorial text-[46px] sm:text-[60px] lg:text-[84px] text-[var(--lu-black)] leading-[0.98]">
              Once in a <span className="italic text-[var(--lu-gold-dark)]">lifetime</span> moments.
            </h2>
            <p className="mt-8 text-[17px] leading-[1.85] text-black-70 font-light">Choose one, combine them or build your own — every concept unfolds in one
              full production designed around your evening.</p>
          </div>
        </Reveal>

        {/* Fan spread */}
        <div
          className="relative mx-auto mt-24"
          style={{
            height: "560px",
            maxWidth: "1200px",
            perspective: "1600px",
          }}
          data-testid="fan-track"
        >
          {cards.map((c, i) => {
            const offset = i - center;
            const rot = offset * 9; // degrees
            const tx = offset * 88; // px horizontal spread
            const ty = Math.abs(offset) * 28; // px vertical lift for side cards
            const isHover = hover === i;
            const dimmed = hover !== null && !isHover;

            const style = {
              transform: isHover
                ? `translate3d(${tx}px, ${-40}px, 0) rotate(0deg) scale(1.08)`
                : `translate3d(${tx}px, ${ty}px, 0) rotate(${rot}deg)`,
              transformOrigin: "50% 100%",
              zIndex: isHover ? 50 : 10 - Math.abs(offset),
              opacity: dimmed ? 0.55 : 1,
              transition:
                "transform 0.7s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.5s ease, box-shadow 0.6s ease",
            };

            return (
              <Link
                key={c.name}
                to={`/concepts/${slugify(c.name)}`}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                data-testid={`fan-card-${i}`}
                className="absolute top-4 left-1/2 -ml-[132px] w-[264px] h-[400px] block border border-[var(--lu-gold-dark)] overflow-hidden shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)] bg-[var(--lu-black)]"
                style={style}
                aria-label={`Concept: ${c.name}`}
              >
                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="tile-cover-overlay" />
                <div
                  className="pointer-events-none absolute inset-2 border border-[var(--lu-gold)]/50"
                  aria-hidden
                />
                <div className="absolute inset-0 flex flex-col justify-end p-5 text-left">
                  <p className="font-micro text-[9px] text-[var(--lu-gold-light)]">{c.num}</p>
                  <h3
                    className="mt-2 font-serif text-[22px] leading-tight text-[var(--lu-ivory)]"
                    style={{ textShadow: "0 2px 12px rgba(0,0,0,0.85)" }}
                  >
                    {c.name}
                  </h3>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-ivory-75 font-light">
                    {c.tags.join(" · ")}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 flex justify-center">
          <Link
            to="/services"
            onClick={() =>
              setTimeout(
                () =>
                  document
                    .getElementById("concepts")
                    ?.scrollIntoView({ behavior: "smooth" }),
                200
              )
            }
            className="btn-gold-outline"
            data-testid="fan-see-all"
          >See all Concepts</Link>
        </div>
      </div>
    </section>
  );
}
